import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Wallet, Eye, Loader2, Save, X, Check, Clock, CreditCard, Send } from "lucide-react";
import api from "@/lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export default function AdminAffiliates() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [rate, setRate] = useState(20);
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState(null);
  const [allMethods, setAllMethods] = useState([]);
  const [enabled, setEnabled] = useState([]);
  const [savingMethods, setSavingMethods] = useState(false);
  const [payModal, setPayModal] = useState(null); // { affiliate, method, reference, note, requestId }
  const [paying, setPaying] = useState(false);
  const [requests, setRequests] = useState([]);
  const [minPayout, setMinPayout] = useState(50);
  const [savingMin, setSavingMin] = useState(false);
  const [approveRates, setApproveRates] = useState({});
  const [actingId, setActingId] = useState(null);
  const [paypalCfg, setPaypalCfg] = useState({ configured: false, mode: "sandbox", client_id_masked: "", has_secret: false });
  const [pplForm, setPplForm] = useState({ client_id: "", secret: "", mode: "sandbox" });
  const [savingPpl, setSavingPpl] = useState(false);
  const [payingPplId, setPayingPplId] = useState(null);
  const [payingBatch, setPayingBatch] = useState(false);
  const [schedule, setSchedule] = useState({ enabled: false, frequency: "weekly", weekday: 1, day_of_month: 1, next_run: null, is_today: false, eligible_count: 0 });
  const [savingSched, setSavingSched] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/affiliates"); setRows(data); }
    catch { toast.error("Failed to load"); } finally { setLoading(false); }
  };
  const loadMethods = async () => {
    try { const { data } = await api.get("/admin/payout-methods"); setAllMethods(data.all || []); setEnabled(data.enabled || []); }
    catch { /* silent */ }
  };
  const loadRequests = async () => {
    try { const { data } = await api.get("/admin/payout-requests?status=requested"); setRequests(data); }
    catch { /* silent */ }
  };
  const loadConfig = async () => {
    try { const { data } = await api.get("/admin/payout-config"); setMinPayout(data.min_payout ?? 50); }
    catch { /* silent */ }
  };
  const loadPaypal = async () => {
    try { const { data } = await api.get("/admin/payouts/paypal"); setPaypalCfg(data); setPplForm((f) => ({ ...f, mode: data.mode || "sandbox" })); }
    catch { /* silent */ }
  };
  const loadSchedule = async () => {
    try { const { data } = await api.get("/admin/payouts/schedule"); setSchedule(data); }
    catch { /* silent */ }
  };
  useEffect(() => { load(); loadMethods(); loadRequests(); loadConfig(); loadPaypal(); loadSchedule(); }, []);

  const saveSchedule = async () => {
    setSavingSched(true);
    try {
      const { data } = await api.put("/admin/payouts/schedule", {
        enabled: schedule.enabled, frequency: schedule.frequency,
        weekday: Number(schedule.weekday), day_of_month: Number(schedule.day_of_month),
      });
      setSchedule((s) => ({ ...s, ...data }));
      toast.success("Pay-day schedule saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSavingSched(false); }
  };

  const savePaypal = async () => {
    setSavingPpl(true);
    try {
      const payload = { mode: pplForm.mode };
      if (pplForm.client_id.trim()) payload.client_id = pplForm.client_id.trim();
      if (pplForm.secret.trim()) payload.secret = pplForm.secret.trim();
      const { data } = await api.put("/admin/payouts/paypal", payload);
      toast.success(`PayPal settings saved (${data.mode})`);
      setPplForm((f) => ({ ...f, client_id: "", secret: "" }));
      loadPaypal();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSavingPpl(false); }
  };
  const payPaypal = async (req) => {
    if (!window.confirm(`Send ${money(req.amount)} to ${req.email} via PayPal (${paypalCfg.mode})?`)) return;
    setPayingPplId(req.id);
    try {
      const { data } = await api.post(`/admin/payout-requests/${req.id}/pay-paypal`);
      toast.success(`Sent ${money(data.amount)} via PayPal · ref ${data.reference}`);
      loadRequests(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "PayPal payout failed"); }
    finally { setPayingPplId(null); }
  };
  const payPaypalBatch = async () => {
    const ids = requests.filter((r) => r.method === "paypal").map((r) => r.id);
    if (!ids.length) return;
    if (!window.confirm(`Send a batched PayPal payout for ${ids.length} request(s) in ${paypalCfg.mode} mode?`)) return;
    setPayingBatch(true);
    try {
      const { data } = await api.post("/admin/payout-requests/pay-paypal-batch", { request_ids: ids });
      toast.success(`Paid ${data.count} affiliate(s) · ${money(data.total)} via PayPal`);
      loadRequests(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Batch payout failed"); }
    finally { setPayingBatch(false); }
  };

  const saveMin = async () => {
    setSavingMin(true);
    try { await api.put("/admin/payout-config", { min_payout: Number(minPayout) }); toast.success("Minimum payout updated"); }
    catch { toast.error("Failed"); } finally { setSavingMin(false); }
  };

  const approveRequest = (req) => {
    const a = rows.find((x) => x.id === req.affiliate_id) || { id: req.affiliate_id, code: req.affiliate_code, email: req.email, name: req.name, stats: { owed: req.amount }, payout_details: { [req.method]: req.details } };
    setPayModal({ affiliate: a, method: req.method, reference: "", note: "", requestId: req.id });
  };
  const rejectRequest = async (req) => {
    const note = prompt(`Reject payout request from ${req.email}? Optional note:`, "");
    if (note === null) return;
    try { await api.post(`/admin/payout-requests/${req.id}/reject`, { note }); toast.success("Request rejected"); loadRequests(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/admin/affiliates", { user_email: email.trim(), commission_rate: Number(rate) });
      toast.success("Affiliate added");
      setEmail(""); setRate(20); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  const updateRate = async (a) => {
    const v = prompt(`Commission rate % for ${a.email}`, a.commission_rate);
    if (v === null) return;
    await api.patch(`/admin/affiliates/${a.id}`, { commission_rate: Number(v) });
    load();
  };
  const toggle = async (a) => {
    await api.patch(`/admin/affiliates/${a.id}`, { status: a.status === "active" ? "suspended" : "active" });
    load();
  };

  const approveAffiliate = async (a) => {
    setActingId(a.id);
    try {
      const rate = Number(approveRates[a.id] ?? a.commission_rate ?? 20);
      const { data } = await api.post(`/admin/affiliates/${a.id}/approve`, { commission_rate: rate });
      toast.success(`Approved — code ${data.code}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setActingId(null); }
  };
  const rejectAffiliate = async (a) => {
    const note = prompt(`Reject affiliate application from ${a.email}? Optional reason:`, "");
    if (note === null) return;
    setActingId(a.id);
    try {
      await api.post(`/admin/affiliates/${a.id}/reject`, { note });
      toast.success("Application rejected");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setActingId(null); }
  };

  const toggleMethod = (id) => {
    setEnabled((e) => (e.includes(id) ? e.filter((m) => m !== id) : [...e, id]));
  };
  const saveMethods = async () => {
    setSavingMethods(true);
    try { await api.put("/admin/payout-methods", { enabled }); toast.success("Payout methods updated"); }
    catch { toast.error("Failed"); } finally { setSavingMethods(false); }
  };

  const openPayout = (a) => {
    if (!a.stats?.owed) { toast.info("Nothing owed"); return; }
    const enabledMethods = allMethods.filter((m) => enabled.includes(m.id));
    const hasSaved = enabledMethods.find((m) => a.payout_details?.[m.id]);
    setPayModal({ affiliate: a, method: hasSaved?.id || enabledMethods[0]?.id || "manual", reference: "", note: "" });
  };
  const confirmPayout = async () => {
    const a = payModal.affiliate;
    const mObj = allMethods.find((m) => m.id === payModal.method);
    const methodLabel = mObj ? mObj.label : payModal.method;
    setPaying(true);
    try {
      let data;
      if (payModal.requestId) {
        ({ data } = await api.post(`/admin/payout-requests/${payModal.requestId}/approve`, { method: methodLabel, reference: payModal.reference, note: payModal.note }));
        loadRequests();
      } else {
        ({ data } = await api.post(`/admin/affiliates/${a.id}/payout`, { method: methodLabel, reference: payModal.reference, note: payModal.note }));
      }
      toast.success(`Paid ${money(data.amount)} across ${data.count} referral(s)`);
      setPayModal(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setPaying(false); }
  };

  return (
    <div data-testid="admin-affiliates">
      <div className="mb-6">
        <p className="overline mb-2">Admin · Growth</p>
        <h1 className="font-editorial text-3xl font-medium tracking-tight text-white sm:text-4xl">Affiliates</h1>
        <p className="mt-2 max-w-2xl text-sm text-emerald-100/60">Add a registered user as an affiliate. They earn commission on referred paid orders (via their link or a coupon tied to their code). Record payouts here — money is sent manually via your own rail.</p>
      </div>

      {/* Pending affiliate applications */}
      {rows.filter((a) => a.status === "pending").length > 0 && (
        <div className="mb-6 card-edge overflow-hidden" data-testid="affiliate-applications">
          <div className="flex items-center gap-2 border-b border-emerald-500/10 px-5 py-4">
            <Clock size={16} className="text-amber-300" /><p className="overline">Affiliate applications</p>
            <span className="chip chip-amber ml-1">{rows.filter((a) => a.status === "pending").length}</span>
          </div>
          <div className="divide-y divide-emerald-500/8">
            {rows.filter((a) => a.status === "pending").map((a) => {
              const acc = a.agreement_accepted || {};
              return (
                <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 text-sm" data-testid={`affiliate-application-${a.id}`}>
                  <div className="min-w-0">
                    <p className="text-white">{a.name || a.email} <span className="text-[11px] text-emerald-100/50">· {a.email}</span></p>
                    <p className="text-[11px] text-emerald-100/50">
                      Agreement v{acc.version || "—"} accepted {acc.accepted_at ? new Date(acc.accepted_at).toLocaleDateString() : "—"}
                      {acc.ip ? ` · IP ${acc.ip}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <input type="number" min="0" step="1"
                        value={approveRates[a.id] ?? a.commission_rate ?? 20}
                        onChange={(e) => setApproveRates((r) => ({ ...r, [a.id]: e.target.value }))}
                        data-testid={`affiliate-approve-rate-${a.id}`}
                        className="w-16 rounded-md border border-emerald-500/15 bg-[#0d2416]/60 px-2 py-1.5 text-sm text-white focus:border-emerald-400/60 focus:outline-none" />
                      <span className="text-xs text-emerald-100/50">%</span>
                    </div>
                    <button onClick={() => approveAffiliate(a)} disabled={actingId === a.id} data-testid={`affiliate-approve-${a.id}`} className="btn-edge text-xs">
                      {actingId === a.id ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Approve
                    </button>
                    <button onClick={() => rejectAffiliate(a)} disabled={actingId === a.id} data-testid={`affiliate-reject-${a.id}`} className="rounded-lg border border-emerald-500/15 px-3 py-1.5 text-xs text-emerald-100/70 hover:border-amber-400/50 hover:text-amber-300"><X size={13} /> Reject</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Payout method settings */}
      <div className="mb-6 card-edge p-5" data-testid="payout-methods-settings">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="overline mb-1">Supported payout methods</p>
            <p className="text-sm text-emerald-100/60">Toggle which methods affiliates can enter details for. Only enabled methods are shown to affiliates.</p>
          </div>
          <button onClick={saveMethods} disabled={savingMethods} data-testid="payout-methods-save" className="btn-edge text-sm">
            {savingMethods ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {allMethods.map((m) => {
            const on = enabled.includes(m.id);
            return (
              <button key={m.id} onClick={() => toggleMethod(m.id)} data-testid={`payout-method-toggle-${m.id}`}
                className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${on ? "border-lime-400/40 bg-lime-400/10 text-lime-300" : "border-emerald-500/15 text-emerald-100/50 hover:text-white"}`}>
                {m.label}
              </button>
            );
          })}
        </div>
        <div className="mt-5 flex flex-wrap items-end gap-3 border-t border-emerald-500/10 pt-4">
          <div>
            <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-emerald-100/60">Minimum payout ($)</label>
            <input type="number" min="0" step="1" value={minPayout} onChange={(e) => setMinPayout(e.target.value)} data-testid="payout-min-input"
              className="w-32 rounded-md border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none" />
          </div>
          <button onClick={saveMin} disabled={savingMin} data-testid="payout-min-save" className="btn-edge-ghost text-sm">
            {savingMin ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save minimum
          </button>
          <p className="text-xs text-emerald-100/50">Affiliates can request a payout once they're owed at least this amount.</p>
        </div>
      </div>

      {/* PayPal Payouts (automated) */}
      <div className="mb-6 card-edge p-5" data-testid="paypal-payout-config">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <CreditCard size={16} className="text-lime-300" /><p className="overline">PayPal Payouts · automated</p>
          <span className={`chip ${paypalCfg.configured ? "chip-emerald" : "chip-neutral"} ml-1`} data-testid="paypal-status">{paypalCfg.configured ? `${paypalCfg.mode} · connected` : "not configured"}</span>
        </div>
        <p className="mb-4 text-xs text-emerald-100/60">Connect a PayPal REST app to send affiliate commissions with one click. PayPal-method requests can be paid instantly (or batched on your chosen days); every other method — and PayPal itself — always keeps the manual "Pay manually" fallback. Get credentials at developer.paypal.com → Apps &amp; Credentials.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-emerald-100/60">Client ID {paypalCfg.client_id_masked ? `(saved: ${paypalCfg.client_id_masked})` : ""}</label>
            <input value={pplForm.client_id} onChange={(e) => setPplForm((f) => ({ ...f, client_id: e.target.value }))} placeholder="PayPal REST app client ID" data-testid="paypal-client-id" className="w-full rounded-md border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none" />
          </div>
          <div>
            <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-emerald-100/60">Secret {paypalCfg.has_secret ? "(saved — leave blank to keep)" : ""}</label>
            <input type="password" value={pplForm.secret} onChange={(e) => setPplForm((f) => ({ ...f, secret: e.target.value }))} placeholder="PayPal REST app secret" data-testid="paypal-secret" className="w-full rounded-md border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none" />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg border border-emerald-500/15 p-0.5" data-testid="paypal-mode-toggle">
            {["sandbox", "live"].map((m) => (
              <button key={m} type="button" onClick={() => setPplForm((f) => ({ ...f, mode: m }))} data-testid={`paypal-mode-${m}`}
                className={`rounded-md px-3 py-1.5 text-xs font-medium capitalize transition-colors ${pplForm.mode === m ? (m === "live" ? "bg-amber-500/20 text-amber-200" : "bg-emerald-500/20 text-lime-200") : "text-emerald-100/60 hover:text-white"}`}>{m}</button>
            ))}
          </div>
          {pplForm.mode === "live" && <span className="text-[11px] text-amber-300/90">Live mode sends real money.</span>}
          <button onClick={savePaypal} disabled={savingPpl} data-testid="paypal-save" className="btn-edge ml-auto text-sm">{savingPpl ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save PayPal settings</button>
        </div>
      </div>

      {/* Scheduled pay-day */}
      <div className="mb-6 card-edge p-5" data-testid="payout-schedule">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Clock size={16} className="text-lime-300" /><p className="overline">Scheduled pay-day</p>
          {schedule.enabled && schedule.next_run && <span className="chip chip-emerald ml-1" data-testid="schedule-next">Next: {new Date(schedule.next_run).toLocaleDateString()}</span>}
        </div>
        <p className="mb-4 text-xs text-emerald-100/60">Pick a day when eligible PayPal payout requests are batched for release. On that day you (and any admin) get a reminder to confirm and send them all with one click — nothing goes out without your confirmation.</p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex items-center gap-2 text-sm text-emerald-100/80">
            <input type="checkbox" checked={schedule.enabled} onChange={(e) => setSchedule((s) => ({ ...s, enabled: e.target.checked }))} data-testid="schedule-enabled" className="h-4 w-4 rounded border-emerald-500/30 accent-lime-400" />
            Enable schedule
          </label>
          <div>
            <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-emerald-100/60">Frequency</label>
            <select value={schedule.frequency} onChange={(e) => setSchedule((s) => ({ ...s, frequency: e.target.value }))} data-testid="schedule-frequency" className="rounded-md border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none">
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          {schedule.frequency === "weekly" ? (
            <div>
              <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-emerald-100/60">Day of week</label>
              <select value={schedule.weekday} onChange={(e) => setSchedule((s) => ({ ...s, weekday: Number(e.target.value) }))} data-testid="schedule-weekday" className="rounded-md border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none">
                {WEEKDAYS.map((d, i) => (<option key={i} value={i}>{d}</option>))}
              </select>
            </div>
          ) : (
            <div>
              <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-emerald-100/60">Day of month (1–28)</label>
              <input type="number" min="1" max="28" value={schedule.day_of_month} onChange={(e) => setSchedule((s) => ({ ...s, day_of_month: e.target.value }))} data-testid="schedule-dom" className="w-24 rounded-md border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none" />
            </div>
          )}
          <button onClick={saveSchedule} disabled={savingSched} data-testid="schedule-save" className="btn-edge ml-auto text-sm">{savingSched ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save schedule</button>
        </div>
      </div>

      {/* Pay-day banner */}
      {schedule.is_today && schedule.eligible_count > 0 && paypalCfg.configured && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-lime-400/40 bg-lime-400/10 p-5" data-testid="payday-banner">
          <div className="flex items-start gap-3">
            <Send size={18} className="mt-0.5 text-lime-300" />
            <div>
              <p className="text-sm font-semibold text-lime-100">It's pay-day</p>
              <p className="text-xs text-lime-100/70">{schedule.eligible_count} eligible PayPal request(s) are ready. Confirm to send them all in one batch.</p>
            </div>
          </div>
          <button onClick={payPaypalBatch} disabled={payingBatch} data-testid="payday-pay-all" className="btn-edge text-sm">{payingBatch ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Pay all now</button>
        </div>
      )}

      {/* Payout requests queue */}
      {requests.length > 0 && (
        <div className="mb-6 card-edge overflow-hidden" data-testid="payout-requests-queue">
          <div className="flex items-center gap-2 border-b border-emerald-500/10 px-5 py-4">
            <Clock size={16} className="text-amber-300" /><p className="overline">Payout requests</p>
            <span className="chip chip-amber ml-1">{requests.length}</span>
            {paypalCfg.configured && requests.some((r) => r.method === "paypal") && (
              <button onClick={payPaypalBatch} disabled={payingBatch} data-testid="payout-paypal-batch" className="btn-edge ml-auto text-xs">
                {payingBatch ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Pay all PayPal ({requests.filter((r) => r.method === "paypal").length})
              </button>
            )}
          </div>
          <div className="divide-y divide-emerald-500/8">
            {requests.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5 text-sm" data-testid={`payout-request-row-${r.id}`}>
                <div className="min-w-0">
                  <p className="text-white">{r.name || r.email} · <span className="font-mono-edge text-lime-300">{r.affiliate_code}</span></p>
                  <p className="text-[11px] text-emerald-100/50">{money(r.amount)} via {r.method_label} · requested {new Date(r.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex gap-2">
                  {r.method === "paypal" && paypalCfg.configured && (
                    <button onClick={() => payPaypal(r)} disabled={payingPplId === r.id} data-testid={`payout-request-paypal-${r.id}`} className="btn-edge text-xs">
                      {payingPplId === r.id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Pay via PayPal
                    </button>
                  )}
                  <button onClick={() => approveRequest(r)} data-testid={`payout-request-approve-${r.id}`} className="rounded-lg border border-emerald-500/15 px-3 py-1.5 text-xs text-emerald-100/80 hover:border-lime-400/50"><Check size={13} /> Pay manually</button>
                  <button onClick={() => rejectRequest(r)} data-testid={`payout-request-reject-${r.id}`} className="rounded-lg border border-emerald-500/15 px-3 py-1.5 text-xs text-emerald-100/70 hover:border-amber-400/50 hover:text-amber-300"><X size={13} /> Reject</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={create} className="mb-6 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-emerald-100/60">User email (must be registered)</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="affiliate-email" placeholder="trader@email.com"
            className="w-full rounded-md border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none" />
        </div>
        <div>
          <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-emerald-100/60">Rate %</label>
          <input type="number" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="affiliate-rate" className="w-24 rounded-md border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white" />
        </div>
        <button disabled={creating} data-testid="affiliate-create" className="btn-edge text-sm">{creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add affiliate</button>
      </form>

      <div className="card-edge overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-emerald-500/10 text-xs uppercase tracking-widest text-emerald-100/50">
              <tr><th className="px-5 py-3">Affiliate</th><th className="px-5 py-3">Code</th><th className="px-5 py-3">Rate</th><th className="px-5 py-3">Orders</th><th className="px-5 py-3">Owed</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="p-8 text-center text-emerald-100/50">Loading…</td></tr>}
              {!loading && rows.filter((a) => a.status === "active" || a.status === "suspended").length === 0 && <tr><td colSpan={7} className="p-8 text-center text-emerald-100/50">No active affiliates yet.</td></tr>}
              {rows.filter((a) => a.status === "active" || a.status === "suspended").map((a) => (
                <tr key={a.id} className="border-b border-emerald-500/8 last:border-0" data-testid={`affiliate-row-${a.id}`}>
                  <td className="px-5 py-3.5 text-white">{a.name}<p className="text-[11px] text-emerald-100/50">{a.email}</p></td>
                  <td className="px-5 py-3.5 font-mono-edge text-lime-300">{a.code}</td>
                  <td className="px-5 py-3.5"><button onClick={() => updateRate(a)} className="text-emerald-100/80 hover:text-lime-300">{a.commission_rate}%</button></td>
                  <td className="px-5 py-3.5 text-emerald-100/70">{a.stats?.orders ?? 0}</td>
                  <td className="px-5 py-3.5 text-lime-300">{money(a.stats?.owed)}</td>
                  <td className="px-5 py-3.5"><button onClick={() => toggle(a)}><span className={`chip ${a.status === "active" ? "chip-emerald" : "chip-neutral"}`}>{a.status}</span></button></td>
                  <td className="px-5 py-3.5">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => { setDetail(a); api.get(`/admin/affiliates/${a.id}/referrals`).then((r) => setDetail({ ...a, refs: r.data })); }} data-testid={`affiliate-view-${a.id}`} className="rounded-lg border border-emerald-500/15 p-1.5 text-emerald-100/80 hover:border-lime-400/50" title="View referrals"><Eye size={14} /></button>
                      <button onClick={() => openPayout(a)} data-testid={`affiliate-payout-${a.id}`} className="rounded-lg border border-emerald-500/15 p-1.5 text-emerald-100/80 hover:border-lime-400/50" title="Record payout"><Wallet size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payout modal */}
      {payModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/60 p-4" onClick={() => setPayModal(null)}>
          <div onClick={(e) => e.stopPropagation()} className="card-moss my-8 w-full max-w-lg overflow-hidden" data-testid="payout-modal">
            <div className="flex items-center justify-between border-b border-emerald-500/12 p-5">
              <div><p className="overline">Record payout · {payModal.affiliate.code}</p><p className="text-sm text-white">{money(payModal.affiliate.stats?.owed)} owed to {payModal.affiliate.email}</p></div>
              <button onClick={() => setPayModal(null)} className="text-emerald-100/60 hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-5">
              <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-emerald-100/60">Payout method</label>
              <select value={payModal.method} onChange={(e) => setPayModal((p) => ({ ...p, method: e.target.value }))} data-testid="payout-modal-method"
                className="w-full rounded-md border border-emerald-500/15 bg-[#061510]/60 px-3 py-2 text-sm text-white">
                {allMethods.filter((m) => enabled.includes(m.id)).map((m) => (
                  <option key={m.id} value={m.id}>{m.label}{payModal.affiliate.payout_details?.[m.id] ? " ✓ details on file" : " (no details)"}</option>
                ))}
                <option value="manual">Other / manual</option>
              </select>

              {payModal.affiliate.payout_details?.[payModal.method] ? (
                <div className="mt-4 rounded-lg border border-emerald-500/12 bg-[#0d2416]/60 p-4" data-testid="payout-modal-details">
                  <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-emerald-100/50">Affiliate's saved details</p>
                  {Object.entries(payModal.affiliate.payout_details[payModal.method]).map(([k, v]) => (
                    v ? <div key={k} className="flex items-center justify-between gap-3 py-1 text-sm">
                      <span className="text-emerald-100/50 capitalize">{k}</span>
                      <button onClick={() => { navigator.clipboard?.writeText(v); toast.success("Copied"); }} className="font-mono-edge text-lime-300 hover:text-lime-200">{v}</button>
                    </div> : null
                  ))}
                </div>
              ) : payModal.method !== "manual" ? (
                <p className="mt-4 text-xs text-amber-300/80">The affiliate hasn't saved details for this method yet.</p>
              ) : null}

              <label className="mb-1.5 mt-4 block text-[10px] font-semibold uppercase tracking-widest text-emerald-100/60">Transaction reference (optional)</label>
              <input value={payModal.reference} onChange={(e) => setPayModal((p) => ({ ...p, reference: e.target.value }))} data-testid="payout-modal-reference" placeholder="e.g. MPESA QGH12ABC / PayPal txn id"
                className="w-full rounded-md border border-emerald-500/15 bg-[#061510]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none" />

              <label className="mb-1.5 mt-4 block text-[10px] font-semibold uppercase tracking-widest text-emerald-100/60">Note (optional)</label>
              <input value={payModal.note} onChange={(e) => setPayModal((p) => ({ ...p, note: e.target.value }))} data-testid="payout-modal-note"
                className="w-full rounded-md border border-emerald-500/15 bg-[#061510]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none" />

              <button onClick={confirmPayout} disabled={paying} data-testid="payout-modal-confirm" className="btn-edge mt-5 w-full justify-center text-sm">
                {paying ? <Loader2 size={14} className="animate-spin" /> : <Wallet size={14} />} Mark {money(payModal.affiliate.stats?.owed)} as paid
              </button>
            </div>
          </div>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/60 p-4" onClick={() => setDetail(null)}>
          <div onClick={(e) => e.stopPropagation()} className="card-moss my-8 flex max-h-[85vh] w-full max-w-xl flex-col overflow-hidden">
            <div className="border-b border-emerald-500/12 p-5"><p className="overline">Referrals · {detail.code}</p><p className="text-sm text-white">{detail.email}</p></div>
            <div className="overflow-y-auto p-5">
              {!detail.refs ? <p className="text-sm text-emerald-100/50">Loading…</p> : detail.refs.length === 0 ? <p className="text-sm text-emerald-100/50">No referrals yet.</p> : (
                <div className="grid gap-2">
                  {detail.refs.map((r) => (
                    <div key={r.id} className="flex items-center justify-between gap-3 rounded-lg border border-emerald-500/12 bg-[#0d2416]/60 p-3 text-sm">
                      <div className="min-w-0"><p className="text-white">{money(r.commission_amount)} <span className="text-[11px] text-emerald-100/50">of {money(r.order_total)} · {r.source} · {r.user_email}</span></p><p className="truncate text-[11px] text-emerald-100/60">{(r.items || []).map((i) => i.title).join(", ") || "—"}</p><p className="text-[11px] text-emerald-100/40">{new Date(r.created_at).toLocaleDateString()}</p></div>
                      <span className={`chip ${r.status === "paid" ? "chip-emerald" : "chip-neutral"}`}>{r.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
